/**
 * `pipeline_load_time_ms` emitter (SE-5508).
 *
 * ## What is being measured, and why this definition
 *
 * SE-5492 characterised pipeline load as **first card visible** — the point at
 * which the board stops being a spinner and the user can act on it — and
 * measured ~889ms on a dev-pointed export with a real browser harness. That
 * measurement was taken with devtools; it left NO instrumentation behind (there
 * is no `performance.mark`, no timing span, and no first-card hook anywhere in
 * `src/`). So this hook does not "reuse a timing point" — none existed — it
 * codifies SE-5492's DEFINITION so the shipped metric and the recorded baseline
 * mean the same thing. Inventing a second definition (say, query-resolved, or
 * board-mounted) would produce a number that could not be compared to the 889ms
 * baseline at all.
 *
 * Start = the board screen's first render. End = the first render at which the
 * board holds at least one card. That brackets query time, cache normalisation
 * and the first card commit, which is what a user experiences as "the pipeline
 * loaded".
 *
 * ## Scope: one measurement per mount, not per board switch
 *
 * The clock starts when the screen mounts, so only the FIRST board to render a
 * card is measured. An in-session board switch reuses an already-warm screen,
 * an already-open Apollo cache and an already-parsed bundle; timing it against
 * the mount clock would report a load that did not happen. It is left
 * unmeasured rather than reported as a fabricated number.
 *
 * A board that legitimately holds zero cards never emits — there is no first
 * card to become visible, so there is no first-card-visible time. That is a
 * real gap in coverage for empty pipelines, not a silent failure.
 * @module features/player-kanban/hooks/usePipelineLoadTime
 */

import { useEffect, useRef } from "react";

import { AnalyticsTracker } from "@/features/analytics";

/**
 * Monotonic-ish clock, preferring `performance.now()` where available.
 *
 * `performance` is absent in some React Native runtimes and in parts of the
 * jest environment, so this degrades to `Date.now()`. Both are only ever used
 * to subtract two readings taken the same way, so the units stay milliseconds
 * either way.
 * @returns The current time in milliseconds.
 */
const nowMs = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

/** Inputs for {@link usePipelineLoadTime}. */
export interface UsePipelineLoadTimeArgs {
  /** The board that rendered, or null/undefined while it is resolving. */
  readonly boardId: string | null | undefined;
  /** How many cards the board currently holds. */
  readonly cardCount: number;
  /**
   * Whether this is a PIPELINE surface. A legacy watchlist/roster board is not
   * a pipeline and must not contribute to the pipeline load metric.
   */
  readonly isPipelineSurface: boolean;
}

/**
 * Emit `pipeline_load_time_ms` once, when the first card becomes visible.
 * @param args - {@link UsePipelineLoadTimeArgs}.
 * @param args.boardId - The board that rendered, or null while it resolves.
 * @param args.cardCount - How many cards the board currently holds.
 * @param args.isPipelineSurface - Whether this is a PIPELINE surface.
 */
export function usePipelineLoadTime({
  boardId,
  cardCount,
  isPipelineSurface,
}: UsePipelineLoadTimeArgs): void {
  /**
   * Mount timestamp. `useRef`'s initial argument is evaluated on every render
   * but only the first is retained, so this is the screen's first render —
   * deliberately NOT set inside an effect, which would run after paint and
   * silently exclude the mount-to-first-paint portion of the load.
   */
  const startedAtRef = useRef<number>(nowMs());

  /**
   * Whether the measurement has been reported. A ref, not state: it must be
   * readable and writable synchronously within one effect pass, and flipping
   * state here would re-render the board screen for a telemetry bookkeeping
   * value nothing renders.
   */
  const emittedRef = useRef(false);

  useEffect(() => {
    if (emittedRef.current) return;
    if (!isPipelineSurface || !boardId) return;
    // First card visible. Until a card exists there is nothing to have become
    // visible, so there is nothing to time.
    if (cardCount <= 0) return;

    emittedRef.current = true;
    void AnalyticsTracker.pipeline.logLoadTime({
      duration_ms: Math.round(nowMs() - startedAtRef.current),
      card_count: cardCount,
      board_id: boardId,
    });
  }, [boardId, cardCount, isPipelineSurface]);
}
